/**
 * Custom Profile Storage Service
 *
 * Manages persistence of custom node and switch profiles with hybrid storage.
 * Supports both API (cloud) and localStorage (offline) storage.
 * Profiles are synced to API when online, with localStorage as fallback.
 */

import type { NodeProfile } from '../types/additional-nodes';
import type { SwitchModel } from '../types/switches';
import { apiClient } from './api/client';
import * as profilesAPI from './api/profiles';

const STORAGE_KEY_NODES = 'gpu-bom-custom-node-profiles';
const STORAGE_KEY_SWITCHES = 'gpu-bom-custom-switch-profiles';

// Flag to enable/disable API usage (can be toggled for offline mode)
let USE_API = true;

/**
 * Enable or disable API usage
 * When disabled, only localStorage will be used
 */
export function setAPIEnabled(enabled: boolean): void {
  USE_API = enabled;
}

/**
 * Check if API is currently enabled and user is authenticated
 */
function shouldUseAPI(): boolean {
  return USE_API && apiClient.isAuthenticated();
}

/**
 * Custom profile wrapper that adds builtin flag and creation metadata
 */
export interface CustomNodeProfile extends NodeProfile {
  builtin?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomSwitchProfile extends SwitchModel {
  builtin?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ===== Node Profile Storage =====

/**
 * Get all custom node profiles from localStorage (private helper)
 */
function getLocalNodeProfiles(): CustomNodeProfile[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_NODES);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to load custom node profiles:', error);
    return [];
  }
}

/**
 * Save node profile to localStorage (private helper)
 */
function saveLocalNodeProfile(profile: Omit<CustomNodeProfile, 'createdAt' | 'updatedAt'>): void {
  try {
    const profiles = getLocalNodeProfiles();
    const now = new Date().toISOString();

    const existingIndex = profiles.findIndex(p => p.id === profile.id);

    if (existingIndex >= 0) {
      // Update existing
      profiles[existingIndex] = {
        ...profile,
        builtin: false,
        createdAt: profiles[existingIndex].createdAt || now,
        updatedAt: now,
      } as CustomNodeProfile;
    } else {
      // Create new
      profiles.push({
        ...profile,
        builtin: false,
        createdAt: now,
        updatedAt: now,
      } as CustomNodeProfile);
    }

    localStorage.setItem(STORAGE_KEY_NODES, JSON.stringify(profiles));
  } catch (error) {
    console.error('Failed to save custom node profile:', error);
    throw new Error('Failed to save profile');
  }
}

/**
 * Get all custom node profiles (hybrid: API first, localStorage fallback)
 */
export async function getCustomNodeProfiles(): Promise<CustomNodeProfile[]> {
  if (shouldUseAPI()) {
    try {
      const profiles = await profilesAPI.getNodeProfiles();
      // Update localStorage cache
      localStorage.setItem(STORAGE_KEY_NODES, JSON.stringify(profiles));
      return profiles as CustomNodeProfile[];
    } catch (error) {
      console.warn('API unavailable for node profiles, using localStorage:', error);
    }
  }

  // Fallback to localStorage
  return getLocalNodeProfiles();
}

/**
 * Get all custom node profiles synchronously from localStorage only
 * Use this for immediate access without API call
 */
export function getCustomNodeProfilesSync(): CustomNodeProfile[] {
  return getLocalNodeProfiles();
}

/**
 * Save a custom node profile (hybrid: saves to localStorage and syncs to API)
 */
export async function saveCustomNodeProfile(profile: Omit<CustomNodeProfile, 'createdAt' | 'updatedAt'>): Promise<void> {
  // Save locally first (instant feedback)
  saveLocalNodeProfile(profile);

  // Sync to API in background if available
  if (shouldUseAPI()) {
    try {
      const existingProfiles = await profilesAPI.getNodeProfiles();
      const existing = existingProfiles.find(p => p.id === profile.id);

      if (existing) {
        await profilesAPI.updateNodeProfile(profile.id, profile as NodeProfile);
      } else {
        await profilesAPI.createNodeProfile(profile as Omit<NodeProfile, 'id'> & { id?: string });
      }
    } catch (error) {
      console.error('Failed to sync node profile to API:', error);
      // Profile still saved locally
    }
  }
}

/**
 * Delete a custom node profile (hybrid: deletes from localStorage and API)
 */
export async function deleteCustomNodeProfile(id: string): Promise<void> {
  // Delete locally first
  try {
    const profiles = getLocalNodeProfiles();
    const filtered = profiles.filter(p => p.id !== id);
    localStorage.setItem(STORAGE_KEY_NODES, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to delete custom node profile from localStorage:', error);
    throw new Error('Failed to delete profile');
  }

  // Delete from API if available
  if (shouldUseAPI()) {
    try {
      await profilesAPI.deleteNodeProfile(id);
    } catch (error) {
      console.error('Failed to delete node profile from API:', error);
      // Already deleted locally, so don't throw
    }
  }
}

/**
 * Check if a node profile ID exists in custom profiles
 */
export function customNodeProfileExists(id: string): boolean {
  const profiles = getLocalNodeProfiles();
  return profiles.some(p => p.id === id);
}

// ===== Switch Profile Storage =====

/**
 * Get all custom switch profiles from localStorage (private helper)
 */
function getLocalSwitchProfiles(): CustomSwitchProfile[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_SWITCHES);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to load custom switch profiles:', error);
    return [];
  }
}

/**
 * Save switch profile to localStorage (private helper)
 */
function saveLocalSwitchProfile(profile: Omit<CustomSwitchProfile, 'createdAt' | 'updatedAt'>): void {
  try {
    const profiles = getLocalSwitchProfiles();
    const now = new Date().toISOString();

    const existingIndex = profiles.findIndex(p => p.id === profile.id);

    if (existingIndex >= 0) {
      // Update existing
      profiles[existingIndex] = {
        ...profile,
        builtin: false,
        createdAt: profiles[existingIndex].createdAt || now,
        updatedAt: now,
      } as CustomSwitchProfile;
    } else {
      // Create new
      profiles.push({
        ...profile,
        builtin: false,
        createdAt: now,
        updatedAt: now,
      } as CustomSwitchProfile);
    }

    localStorage.setItem(STORAGE_KEY_SWITCHES, JSON.stringify(profiles));
  } catch (error) {
    console.error('Failed to save custom switch profile:', error);
    throw new Error('Failed to save profile');
  }
}

/**
 * Get all custom switch profiles (hybrid: API first, localStorage fallback)
 */
export async function getCustomSwitchProfiles(): Promise<CustomSwitchProfile[]> {
  if (shouldUseAPI()) {
    try {
      const profiles = await profilesAPI.getSwitchProfiles();
      // Update localStorage cache
      localStorage.setItem(STORAGE_KEY_SWITCHES, JSON.stringify(profiles));
      return profiles as CustomSwitchProfile[];
    } catch (error) {
      console.warn('API unavailable for switch profiles, using localStorage:', error);
    }
  }

  // Fallback to localStorage
  return getLocalSwitchProfiles();
}

/**
 * Get all custom switch profiles synchronously from localStorage only
 * Use this for immediate access without API call
 */
export function getCustomSwitchProfilesSync(): CustomSwitchProfile[] {
  return getLocalSwitchProfiles();
}

/**
 * Save a custom switch profile (hybrid: saves to localStorage and syncs to API)
 */
export async function saveCustomSwitchProfile(profile: Omit<CustomSwitchProfile, 'createdAt' | 'updatedAt'>): Promise<void> {
  // Save locally first (instant feedback)
  saveLocalSwitchProfile(profile);

  // Sync to API in background if available
  if (shouldUseAPI()) {
    try {
      const existingProfiles = await profilesAPI.getSwitchProfiles();
      const existing = existingProfiles.find(p => p.id === profile.id);

      if (existing) {
        await profilesAPI.updateSwitchProfile(profile.id, profile as SwitchModel);
      } else {
        await profilesAPI.createSwitchProfile(profile as Omit<SwitchModel, 'id'> & { id?: string });
      }
    } catch (error) {
      console.error('Failed to sync switch profile to API:', error);
      // Profile still saved locally
    }
  }
}

/**
 * Delete a custom switch profile (hybrid: deletes from localStorage and API)
 */
export async function deleteCustomSwitchProfile(id: string): Promise<void> {
  // Delete locally first
  try {
    const profiles = getLocalSwitchProfiles();
    const filtered = profiles.filter(p => p.id !== id);
    localStorage.setItem(STORAGE_KEY_SWITCHES, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to delete custom switch profile from localStorage:', error);
    throw new Error('Failed to delete profile');
  }

  // Delete from API if available
  if (shouldUseAPI()) {
    try {
      await profilesAPI.deleteSwitchProfile(id);
    } catch (error) {
      console.error('Failed to delete switch profile from API:', error);
      // Already deleted locally, so don't throw
    }
  }
}

/**
 * Check if a switch profile ID exists in custom profiles
 */
export function customSwitchProfileExists(id: string): boolean {
  const profiles = getLocalSwitchProfiles();
  return profiles.some(p => p.id === id);
}

/**
 * Clear all custom profiles (useful for reset)
 */
export function clearAllCustomProfiles(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_NODES);
    localStorage.removeItem(STORAGE_KEY_SWITCHES);
  } catch (error) {
    console.error('Failed to clear custom profiles:', error);
  }
}

/**
 * Export all custom profiles as JSON
 */
export async function exportCustomProfiles(): Promise<string> {
  const nodes = await getCustomNodeProfiles();
  const switches = await getCustomSwitchProfiles();
  return JSON.stringify({ nodes, switches }, null, 2);
}

/**
 * Export custom profiles synchronously from localStorage only
 */
export function exportCustomProfilesSync(): string {
  const nodes = getLocalNodeProfiles();
  const switches = getLocalSwitchProfiles();
  return JSON.stringify({ nodes, switches }, null, 2);
}

/**
 * Import custom profiles from JSON (hybrid: saves to both localStorage and API)
 */
export async function importCustomProfiles(json: string): Promise<{ nodes: number; switches: number }> {
  try {
    const data = JSON.parse(json);
    let nodeCount = 0;
    let switchCount = 0;

    if (data.nodes && Array.isArray(data.nodes)) {
      for (const profile of data.nodes) {
        await saveCustomNodeProfile(profile);
        nodeCount++;
      }
    }

    if (data.switches && Array.isArray(data.switches)) {
      for (const profile of data.switches) {
        await saveCustomSwitchProfile(profile);
        switchCount++;
      }
    }

    return { nodes: nodeCount, switches: switchCount };
  } catch (error) {
    console.error('Failed to import custom profiles:', error);
    throw new Error('Invalid import file format');
  }
}

/**
 * Sync local profiles to API
 * Useful for syncing offline changes when coming back online
 */
export async function syncLocalProfilesToAPI(): Promise<{ nodes: number; switches: number }> {
  if (!shouldUseAPI()) {
    return { nodes: 0, switches: 0 };
  }

  let nodeCount = 0;
  let switchCount = 0;

  try {
    const localNodes = getLocalNodeProfiles();
    for (const profile of localNodes) {
      try {
        const existingProfiles = await profilesAPI.getNodeProfiles();
        const existing = existingProfiles.find(p => p.id === profile.id);

        if (existing) {
          await profilesAPI.updateNodeProfile(profile.id, profile as NodeProfile);
        } else {
          await profilesAPI.createNodeProfile(profile as Omit<NodeProfile, 'id'> & { id?: string });
        }
        nodeCount++;
      } catch (error) {
        console.error(`Failed to sync node profile ${profile.id}:`, error);
      }
    }

    const localSwitches = getLocalSwitchProfiles();
    for (const profile of localSwitches) {
      try {
        const existingProfiles = await profilesAPI.getSwitchProfiles();
        const existing = existingProfiles.find(p => p.id === profile.id);

        if (existing) {
          await profilesAPI.updateSwitchProfile(profile.id, profile as SwitchModel);
        } else {
          await profilesAPI.createSwitchProfile(profile as Omit<SwitchModel, 'id'> & { id?: string });
        }
        switchCount++;
      } catch (error) {
        console.error(`Failed to sync switch profile ${profile.id}:`, error);
      }
    }
  } catch (error) {
    console.error('Failed to sync local profiles to API:', error);
  }

  return { nodes: nodeCount, switches: switchCount };
}
